/**
 * Centralized environment configuration module
 *
 * Provides environment detection and flags for the entire application.
 * Use this module instead of directly accessing import.meta.env throughout the app.
 */

export const ENV = {
  // Vercel-specific detection
  isVercel: !!import.meta.env.VITE_VERCEL_URL,
  vercelEnv: import.meta.env.VITE_VERCEL_ENV as 'production' | 'preview' | 'development' | undefined,

  // Build mode detection
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,

  // Computed flags
  get isProductionDeploy(): boolean {
    return this.isVercel && this.vercelEnv === 'production';
  },
  get isStagingDeploy(): boolean {
    return this.isVercel && this.vercelEnv === 'preview' && this.mode === 'staging';
  },
  get isLocalDevelopment(): boolean {
    return !this.isVercel && this.isDevelopment;
  },
  get shouldEnableLogging(): boolean {
    return !this.isProduction; // Disable in any production build
  }
};

/**
 * UI feature flags evaluated at build time.
 */
// Runtime "preview" override: staff can opt into gated features without a Vercel
// rebuild, via localStorage `preview:<key>` (flipped from the staff Preview
// Features tab). The build-time VITE_* var still force-enables per environment.
// Read once at module load — toggling requires a reload to take effect.
// ticketsBoard was removed 2026-09-10: the Support Tickets board is now
// unconditional. It shipped 2026-08-02 and sat dark behind VITE_TICKETS_BOARD
// for five weeks, which is why filed tickets appeared to vanish -- the merged
// board is the only UI that lists the `tickets` table.
const PREVIEW_KEYS = ['competitorV2', 'pagegenDsh'] as const;
export type PreviewFlagKey = (typeof PREVIEW_KEYS)[number];
export const PREVIEW_FLAG_KEYS = PREVIEW_KEYS;

function previewOverride(key: PreviewFlagKey): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(`preview:${key}`) === 'true';
  } catch {
    return false;
  }
}

function flag(envOn: boolean, key: PreviewFlagKey): boolean {
  return envOn || previewOverride(key);
}

export function isPreviewOverrideOn(key: PreviewFlagKey): boolean {
  return previewOverride(key);
}

export function setPreviewOverride(key: PreviewFlagKey, on: boolean): void {
  try {
    if (on) localStorage.setItem(`preview:${key}`, 'true');
    else localStorage.removeItem(`preview:${key}`);
  } catch {
    /* ignore */
  }
}

/**
 * Which Emjae chat surface the app shell mounts.
 *  - `'header'` (DEFAULT): a badger button in the top bar, next to the notification bell,
 *    that slides Carl's side panel in from the right and pushes the page.
 *  - `'bubble'`: the floating corner badger that opens a floating window.
 *  - `'rail'`: the always-docked right-side rail (Carl's design, 2026-09-29).
 *
 * Build-time `VITE_CHAT_SURFACE`. Exactly `'rail'` gives the rail and exactly `'bubble'`
 * gives the bubble; unset, empty or anything else (including `'header'`) is the header.
 * Because unset is the header, setting nothing in an environment CHANGES it to the header:
 * set the value explicitly in every Vercel environment (production, staging, preview) and
 * rebuild, since Vite inlines it at build time.
 */
export type ChatSurface = 'bubble' | 'rail' | 'header';

export function parseChatSurface(raw: string | undefined): ChatSurface {
  if (raw === 'rail') return 'rail';
  if (raw === 'bubble') return 'bubble';
  return 'header';
}

export const CHAT_SURFACE: ChatSurface = parseChatSurface(import.meta.env.VITE_CHAT_SURFACE);

export const FEATURE_FLAGS = {
  /**
   * Brand Bible field-level AI Suggest controls. The manual A-I editor does
   * not depend on this enhancement, so production keeps it hidden until the
   * request/grounding contract passes its own release gate.
   */
  brandBibleAiSuggest: import.meta.env.VITE_BRAND_BIBLE_AI_SUGGEST_ENABLED === 'true',

  /**
   * Brand Bible file upload/reader surfaces. Kept independent from the manual
   * editor release because its storage migrations and reader QA roll out on a
   * separate track.
   */
  brandBibleFileIngestion: import.meta.env.VITE_BRAND_BIBLE_FILE_INGESTION_ENABLED === 'true',

  /**
   * Whether the "Content Preferences" UI is EDITABLE. The page is always shown,
   * but until the preferences are wired into the backend generation pipeline it
   * is rendered read-only (locked) everywhere by default — so the SEO team can
   * see it but can't change settings that have no effect yet. A developer
   * working on the feature opts in to editing by setting
   * VITE_CONTENT_PREFERENCES_ENABLED=true (e.g. in frontend/.env.local).
   */
  contentPreferencesEditable: import.meta.env.VITE_CONTENT_PREFERENCES_ENABLED === 'true',

  /**
   * The landing-page generator. DEFAULT ON, which is the opposite of every
   * other flag here and deliberate: this gate exists to REMOVE the surface
   * from an environment that cannot serve it, not to stage a rollout.
   *
   * ⚠ THE ROUTE IS USELESS WITHOUT SEEDED TEMPLATES. The page HTML lives in
   * `section_template_versions`, not in the bundle, so an environment that has
   * the code but has never run scripts/seed_landing_templates.py renders the
   * whole form and can then build nothing. Staging was in exactly that state
   * on 2026-08-19: the UI shipped there, zero landing template rows existed,
   * and the screen looked available while being unusable — worse than absent,
   * because an operator has no way to tell which it is.
   *
   * Set VITE_LANDING_PAGES_ENABLED=false to hide it. Turn it back on in the
   * same change that seeds the templates, not before.
   */
  landingPagesEnabled: import.meta.env.VITE_LANDING_PAGES_ENABLED !== 'false',

  /** Audit → Proposal tool (internal, access-gated). Off by default. */
  proposalToolEnabled: import.meta.env.VITE_PROPOSAL_TOOL_ENABLED === 'true',

  /**
   * Competitor Comparison rebuild (nav-redesign row 10). Adds a "Top Pages" tab:
   * auto-fills the tenant's own domain, accepts competitor URLs, and renders an
   * Ahrefs traffic chart of top pages with an "add to keyword pipeline" action.
   * Off by default; the existing 3-tab competitor page is unchanged.
   */
  competitorV2: flag(import.meta.env.VITE_COMPETITOR_V2 === 'true', 'competitorV2'),

  pagegenDsh: flag(import.meta.env.VITE_PAGEGEN_DSH !== 'false', 'pagegenDsh'),
};
